import { useEffect, useMemo, useRef, useState } from "react";
import { useCustom, useGetLocale, usePermissions, useTranslate } from "@refinedev/core";
import {
  Button,
  Field,
  FileIcon,
  PencilIcon,
  PlusCircleSolidIcon,
  TrashIcon,
  ImagePreview,
  Modal,
  SelectField,
  type Column,
} from "@fawkes/ui";

import { formatDateTime } from "../../i18n/format";
import { mediaTypeLabel, mediaUsageLabel } from "./commLabels";
import {
  bewerkCommMedia,
  CommMediaApiError,
  deleteCommMedia,
  fetchCommMediaBlob,
  uploadCommMedia,
} from "./commMediaApi";
import { IndeelbareTabel } from "../../components/IndeelbareTabel";

// communicatie media-library list — GET communicatie/media (RELATIVE) with the server-side
// type/usage/q filters. Thumbnails are now real: the binary is served by api/ and fetched AUTHENTICATED
// (bearer-only token, not a plain <img src>) as a blob → object URL, mirroring the animal media tile.
// Editor-gated upload + delete; viewers see read-only metadata + thumbnails.

const EDITOR_ROLE = "ROLE_COMMUNICATIE_EDITOR";

interface CommMediaItem {
  id: string;
  name: string;
  fileRef: string | null;
  fileName: string | null;
  contentType: string | null;
  fileSizeBytes: number | null;
  mediaType: string | null;
  usage: string | null;
  vestigingId: number | null;
  defaultWidthPx: number | null;
  createdDate: string | null;
  createdByUser: string | null;
}

function isImage(contentType: string | null): boolean {
  return !!contentType && contentType.startsWith("image/");
}

function formatBytes(bytes: number | null): string {
  if (bytes == null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// An authenticated image thumbnail: fetch the bytes as a blob → object URL (revoked on unmount). Non-
// images fall back to a file glyph. Same pattern as the animal media tile's MediaThumb.
function MediaThumb({ item }: { item: CommMediaItem }) {
  const [url, setUrl] = useState<string | null>(null);
  const image = isImage(item.contentType);
  useEffect(() => {
    if (!image) return;
    let revoked = false;
    let objectUrl: string | null = null;
    fetchCommMediaBlob(item.id)
      .then((blob) => {
        if (revoked) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => undefined);
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [item.id, image]);

  if (!image) {
    return (
      <span className="comm-read-media__thumb comm-read-media__thumb--doc" aria-hidden="true">
        <FileIcon width={24} height={24} />
      </span>
    );
  }
  return (
    <ImagePreview
      src={url}
      alt={item.fileName ?? item.name}
      size="48px"
      className="comm-read-media__thumb"
    />
  );
}

export function CommMediaList() {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const { data: permissions } = usePermissions<string[]>();
  const canEdit = Array.isArray(permissions) && permissions.includes(EDITOR_ROLE);

  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [type, setType] = useState("");
  const [usage, setUsage] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    const handle = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(handle);
  }, [query]);

  const url = useMemo(() => {
    const params = new URLSearchParams();
    if (type) params.set("type", type);
    if (usage) params.set("usage", usage);
    if (debounced) params.set("q", debounced);
    const qs = params.toString();
    return qs ? `communicatie/media?${qs}` : "communicatie/media";
  }, [type, usage, debounced]);

  const { data, isLoading, isError, refetch } = useCustom<CommMediaItem[]>({ url, method: "get" });
  const rows = data?.data ?? [];

  const typeOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.mediaType).filter((x): x is string => !!x))).sort(),
    [rows],
  );
  const usageOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.usage).filter((x): x is string => !!x))).sort(),
    [rows],
  );

  // Het item dat op dit moment bewerkt wordt; null = geen dialoog open.
  const [bewerken, setBewerken] = useState<CommMediaItem | null>(null);

  async function handleDelete(item: CommMediaItem) {
    setNotice(null);
    try {
      await deleteCommMedia(item.id);
      setNotice(t("communicatieRead.media.deleted"));
      await refetch();
    } catch (e) {
      setNotice(
        e instanceof CommMediaApiError
          ? t("communicatieRead.media.deleteError")
          : t("communicatieRead.media.deleteError"),
      );
    }
  }

  // Eén rij tegelijk gekozen; de knoppen erboven werken daarop.
  const [gekozen, setGekozen] = useState<string | null>(null);

  const columns: Column<CommMediaItem>[] = [
    {
      header: t("communicatieRead.media.column.preview"),
      cell: (r) => <MediaThumb item={r} />,
    },
    {
      header: t("communicatieRead.media.column.name"),
      cell: (r) => <span className="comm-read-media__name">{r.name}</span>,
    },
    { header: t("communicatieRead.media.column.type"), cell: (r) => mediaTypeLabel(r.mediaType, t) },
    { header: t("communicatieRead.media.column.usage"), cell: (r) => mediaUsageLabel(r.usage, t) },
    { header: t("communicatieRead.media.column.fileName"), cell: (r) => r.fileName ?? "—" },
    { header: t("communicatieRead.media.column.size"), cell: (r) => formatBytes(r.fileSizeBytes) },
    {
      header: t("communicatieRead.media.column.defaultWidth"),
      cell: (r) => (r.defaultWidthPx != null ? `${r.defaultWidthPx} px` : "—"),
    },
    {
      header: t("communicatieRead.media.column.created"),
      cell: (r) => formatDateTime(r.createdDate, locale),
    },
  ];

  const gekozenItem = rows.find((r) => r.id === gekozen) ?? null;

  return (
    <div className="comm-list">
      {notice && (
        <div className="comm-editor__alert comm-editor__alert--ok" role="status">
          {notice}
        </div>
      )}

      <div className="comm-list-filters">
        <div className="comm-list-filter">
          <label htmlFor="comm-media-zoek">{t("communicatieRead.media.filter.search")}</label>
          <input
            id="comm-media-zoek"
            type="search"
            autoComplete="off"
            className="fui-field__input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="comm-list-filter">
          <label htmlFor="comm-media-type">{t("communicatieRead.media.filter.type")}</label>
          <select
            id="comm-media-type"
            className="fui-field__input"
            value={type}
            onChange={(e) => setType(e.target.value)}
          >
            <option value="">{t("communicatieRead.filter.all")}</option>
            {typeOptions.map((c) => (
              <option key={c} value={c}>
                {mediaTypeLabel(c, t)}
              </option>
            ))}
          </select>
        </div>
        <div className="comm-list-filter">
          <label htmlFor="comm-media-usage">{t("communicatieRead.media.filter.usage")}</label>
          <select
            id="comm-media-usage"
            className="fui-field__input"
            value={usage}
            onChange={(e) => setUsage(e.target.value)}
          >
            <option value="">{t("communicatieRead.filter.all")}</option>
            {usageOptions.map((c) => (
              <option key={c} value={c}>
                {mediaUsageLabel(c, t)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Aanmaken, bewerken en verwijderen boven de tabel, werkend op de gekozen rij — zoals de
          bron. Uit tot je een rij hebt gekozen, zodat je ziet dat ze op een keuze wachten in
          plaats van dat er niets gebeurt. */}
      {canEdit && (
        <div className="comm-list-acties">
          <Button onClick={() => setShowUpload(true)} disabled={isLoading}>
            <PlusCircleSolidIcon width={16} height={16} /> {t("communicatieRead.media.upload")}
          </Button>
          <Button
            variant="secondary"
            disabled={!gekozenItem}
            onClick={() => gekozenItem && setBewerken(gekozenItem)}
          >
            <PencilIcon width={15} height={15} /> {t("communicatieRead.media.edit")}
          </Button>
          <Button
            variant="secondary"
            disabled={!gekozenItem}
            onClick={() => gekozenItem && handleDelete(gekozenItem)}
          >
            <TrashIcon width={15} height={15} /> {t("communicatieRead.media.delete")}
          </Button>
        </div>
      )}

      {isLoading ? (
        <p className="fawkes-muted">{t("common.loading")}</p>
      ) : isError ? (
        <p className="fawkes-error">{t("communicatieRead.loadFailed")}</p>
      ) : (
        <div className="comm-list-grid">
          <IndeelbareTabel tabelKey="communicatie-comm-media-list"
            columns={columns}
            rows={rows}
            getRowKey={(r) => r.id}
            empty={t("communicatieRead.media.empty")}
            selectedRowKey={gekozen ?? undefined}
            onRowClick={(r) => setGekozen(r.id)}
          />
        </div>
      )}

      {canEdit && (
        <UploadDialog
          open={showUpload}
          onClose={() => setShowUpload(false)}
          onUploaded={async () => {
            setShowUpload(false);
            setNotice(t("communicatieRead.media.uploaded"));
            await refetch();
          }}
          t={t}
        />
      )}

      {canEdit && bewerken && (
        <BewerkDialog
          item={bewerken}
          onClose={() => setBewerken(null)}
          onBewerkt={async () => {
            setBewerken(null);
            setNotice(t("communicatieRead.media.edited"));
            await refetch();
          }}
          t={t}
        />
      )}
    </div>
  );
}

/** De vier gegevens die de bron in zijn detailscherm laat bijstellen. */
const MEDIA_SOORTEN = ["LOGO", "AFBEELDING", "HANDTEKENING", "PICTOGRAM", "QRCODE"];
const MEDIA_GEBRUIK = ["BEIDE", "EMAIL", "PRINT"];

/**
 * Een media-item bijstellen (de Jmix {@code MediaItemDetailView}): naam, soort, waar het gebruikt mag
 * worden en hoe breed het standaard wordt ingevoegd. Het bestand staat er niet bij — een andere
 * afbeelding is een nieuwe upload, en het item vervangen zou elk sjabloon dat ernaar verwijst zijn
 * afbeelding kosten.
 */
function BewerkDialog({
  item,
  onClose,
  onBewerkt,
  t,
}: {
  item: CommMediaItem;
  onClose: () => void;
  onBewerkt: () => Promise<void>;
  t: ReturnType<typeof useTranslate>;
}) {
  const [naam, setNaam] = useState(item.name ?? "");
  const [soort, setSoort] = useState(item.mediaType ?? "AFBEELDING");
  const [gebruik, setGebruik] = useState(item.usage ?? "BEIDE");
  const [breedte, setBreedte] = useState(item.defaultWidthPx != null ? String(item.defaultWidthPx) : "");
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);

  async function opslaan() {
    setFout(null);
    if (!naam.trim()) {
      setFout(t("communicatieRead.media.edit.naamVerplicht"));
      return;
    }
    const px = breedte.trim() === "" ? null : Number(breedte.trim());
    if (px != null && (!Number.isFinite(px) || px <= 0)) {
      setFout(t("communicatieRead.media.edit.breedteOngeldig"));
      return;
    }
    setBezig(true);
    try {
      await bewerkCommMedia(item.id, {
        name: naam.trim(),
        mediaType: soort,
        usage: gebruik,
        defaultWidthPx: px,
      });
      await onBewerkt();
    } catch {
      setFout(t("communicatieRead.media.edit.fout"));
    } finally {
      setBezig(false);
    }
  }

  return (
    <Modal
      open
      title={t("communicatieRead.media.edit.titel")}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={bezig}>
            {t("common.cancel")}
          </Button>
          <Button variant="primary" onClick={opslaan} disabled={bezig}>
            {t("common.save")}
          </Button>
        </>
      }
    >
      {/* Welk bestand het is staat erbij maar is niet te wijzigen: dat maakt meteen duidelijk dat
          je de gegevens bijstelt en niet de afbeelding vervangt. */}
      <p className="comm-read-media__bestand">
        {t("communicatieRead.media.edit.bestand", { args: [item.fileName ?? "\u2014"] })}
      </p>
      <Field
        label={t("communicatieRead.media.upload.name")}
        type="text"
        value={naam}
        onChange={(e) => setNaam(e.target.value)}
      />
      <SelectField
        label={t("communicatieRead.media.filter.type")}
        value={soort}
        onChange={(e) => setSoort(e.target.value)}
        options={MEDIA_SOORTEN.map((c) => ({ value: c, label: mediaTypeLabel(c, t) }))}
      />
      <SelectField
        label={t("communicatieRead.media.filter.usage")}
        value={gebruik}
        onChange={(e) => setGebruik(e.target.value)}
        options={MEDIA_GEBRUIK.map((c) => ({ value: c, label: mediaUsageLabel(c, t) }))}
      />
      <Field
        label={t("communicatieRead.media.column.defaultWidth")}
        type="text"
        inputMode="numeric"
        value={breedte}
        placeholder={t("communicatieRead.media.edit.breedteLeeg")}
        onChange={(e) => setBreedte(e.target.value)}
      />
      {fout && (
        <div className="comm-editor__alert comm-editor__alert--error" role="alert">
          {fout}
        </div>
      )}
    </Modal>
  );
}

function UploadDialog({
  open,
  onClose,
  onUploaded,
  t,
}: {
  open: boolean;
  onClose: () => void;
  onUploaded: () => void | Promise<void>;
  t: ReturnType<typeof useTranslate>;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [mediaType, setMediaType] = useState("AFBEELDING");
  const [usage, setUsage] = useState("BEIDE");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setFile(null);
    setName("");
    setMediaType("AFBEELDING");
    setUsage("BEIDE");
    setError(null);
    if (fileInput.current) fileInput.current.value = "";
  }

  function close() {
    reset();
    onClose();
  }

  async function submit() {
    setError(null);
    if (!file) {
      setError(t("communicatieRead.media.upload.fileRequired"));
      return;
    }
    setBusy(true);
    try {
      await uploadCommMedia(file, { name: name.trim() || file.name, mediaType, usage });
      reset();
      await onUploaded();
    } catch {
      setError(t("communicatieRead.media.upload.error"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      title={t("communicatieRead.media.upload.title")}
      onClose={close}
      footer={
        <>
          <Button variant="secondary" onClick={close} disabled={busy}>
            {t("common.cancel")}
          </Button>
          <Button variant="primary" onClick={submit} disabled={busy || !file}>
            {t("communicatieRead.media.upload.submit")}
          </Button>
        </>
      }
    >
      <label className="fui-field">
        <span className="fui-field__label">{t("communicatieRead.media.upload.file")}</span>
        <input
          ref={fileInput}
          className="fui-field__input"
          type="file"
          aria-label={t("communicatieRead.media.upload.file")}
          onChange={(e) => {
            const f = e.target.files?.[0] ?? null;
            setFile(f);
            if (f && !name) setName(f.name);
          }}
        />
      </label>
      <Field
        label={t("communicatieRead.media.upload.name")}
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <SelectField
        label={t("communicatieRead.media.filter.type")}
        value={mediaType}
        onChange={(e) => setMediaType(e.target.value)}
        options={[
          { value: "LOGO", label: mediaTypeLabel("LOGO", t) },
          { value: "AFBEELDING", label: mediaTypeLabel("AFBEELDING", t) },
          { value: "HANDTEKENING", label: mediaTypeLabel("HANDTEKENING", t) },
          { value: "PICTOGRAM", label: mediaTypeLabel("PICTOGRAM", t) },
          { value: "QRCODE", label: mediaTypeLabel("QRCODE", t) },
        ]}
      />
      <SelectField
        label={t("communicatieRead.media.filter.usage")}
        value={usage}
        onChange={(e) => setUsage(e.target.value)}
        options={[
          { value: "BEIDE", label: mediaUsageLabel("BEIDE", t) },
          { value: "EMAIL", label: mediaUsageLabel("EMAIL", t) },
          { value: "PRINT", label: mediaUsageLabel("PRINT", t) },
        ]}
      />
      {error && (
        <div className="comm-editor__alert comm-editor__alert--error" role="alert">
          {error}
        </div>
      )}
    </Modal>
  );
}
